import React from 'react';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Clock, CheckCircle2, Circle } from 'lucide-react';

// Wave 3.78 — Colour tokens for delay classification.
// Uses the design_guidelines "data_viz_palette" + material tokens so it
// blends with the rest of the portal (no raw hexes for on-track green).
export const DELAY_COLORS = {
  on_track: { bg: '#EAF7F5', text: '#0F766E', border: '#B7E4DA' },   // series_2_emerald
  at_risk:  { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },   // warm amber (existing on-hold palette)
  delayed:  { bg: '#FEE2E2', text: '#B91C1C', border: '#FCA5A5' },   // destructive tint
  severe:   { bg: '#450A0A', text: '#FEE2E2', border: '#B91C1C' },   // deep destructive for 2× SLA
  not_started: { bg: '#F1F5F9', text: '#475569', border: '#E2E8F0' },
  completed: { bg: '#EAF7F5', text: '#0F766E', border: '#B7E4DA' },
  active:    { bg: '#EAF3FF', text: '#1E4E8C', border: '#CFE3FF' },
  // Wave 3.80 — Blocked/waiting palette. Neutral-slate with a subtle
  // amber-tinted outline so it reads as "paused" not "danger".
  blocked:   { bg: '#F8FAFC', text: '#78716C', border: '#D6D3D1' },
  // Wave 3.83 — SLA Off palette. Muted stone with a neutral outline to
  // signal "intentionally paused / not tracked".
  sla_off:   { bg: '#F5F5F4', text: '#57534E', border: '#D6D3D1' },
};

export function classifyOrder(order) {
  if (!order) return 'not_started';
  if (order.is_severe) return 'severe';
  if (order.is_delayed) return 'delayed';
  if (order.is_at_risk) return 'at_risk';
  return 'on_track';
}

export function DelayBadge({ order, size = 'md' }) {
  const kind = classifyOrder(order);
  const c = DELAY_COLORS[kind];
  const label = {
    on_track: 'On Track',
    at_risk: 'At Risk',
    delayed: 'Delayed',
    severe: 'Critical',
  }[kind] || 'On Track';
  const icon = {
    on_track: <CheckCircle2 size={12} />,
    at_risk: <Clock size={12} />,
    delayed: <AlertTriangle size={12} />,
    severe: <AlertTriangle size={12} />,
  }[kind];
  return (
    <Badge
      style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
      className={`inline-flex items-center gap-1 ${size === 'sm' ? 'text-[10px] px-1.5 py-0' : 'text-[11px]'}`}
      data-testid={`prod-timeline-delay-badge-${kind}`}
    >
      {icon}
      <span>{label}</span>
    </Badge>
  );
}

export function StageDot({ status = 'not_started', size = 10 }) {
  const c = DELAY_COLORS[status] || DELAY_COLORS.not_started;
  if (status === 'completed') {
    return <CheckCircle2 size={size + 2} style={{ color: c.text }} />;
  }
  if (status === 'active') {
    return (
      <span
        className="inline-block rounded-full ring-2 ring-offset-1 ring-offset-white"
        style={{ background: c.text, width: size, height: size, boxShadow: `0 0 0 3px ${c.bg}` }}
      />
    );
  }
  if (status === 'blocked') {
    // Hollow ring with dashed border — visually reads as "paused/waiting".
    return (
      <span
        className="inline-block rounded-full"
        style={{
          width: size + 2, height: size + 2,
          border: `2px dashed ${c.border}`, background: c.bg,
        }}
        aria-label="Blocked stage"
      />
    );
  }
  return (
    <Circle size={size + 2} style={{ color: c.border }} strokeWidth={2} />
  );
}

export function formatHours(hours) {
  if (hours === null || hours === undefined) return '—';
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return '0h';
  if (n < 24) return `${n.toFixed(1)}h`;
  const days = n / 24;
  if (days < 10) return `${days.toFixed(1)}d`;
  return `${Math.round(days)}d`;
}
